import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { vocabularyTiers, getAllVocabularyTiers, analyzeQuestionComplexity } from '../../data/vocabularyTiers';

const { FiInfo, FiChevronDown, FiChevronUp, FiBookOpen, FiLayers, FiCheckCircle, FiAlertTriangle } = FiIcons;

const VocabularyTierHelper = ({ selectedTier, onTierChange, questionText, questionType, showAnalysis = true }) => {
  const [showHelper, setShowHelper] = useState(false);
  const [expandedTier, setExpandedTier] = useState(null);
  
  const tiers = getAllVocabularyTiers();
  const currentTier = vocabularyTiers[selectedTier];

  // Analyze vocabulary complexity
  const analyzeComplexity = () => {
    if (!questionText || !questionType) return null;
    
    const analysis = analyzeQuestionComplexity(questionText, questionType);
    const suggestedTier = vocabularyTiers[analysis.tier];
    
    return {
      suggestedTier: analysis.tier,
      isAligned: selectedTier === analysis.tier,
      confidence: analysis.confidence,
      suggestion: suggestedTier,
      factors: analysis.factors
    };
  };

  const analysis = showAnalysis ? analyzeComplexity() : null;

  return (
    <div className="space-y-4">
      {/* Tier Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Vocabulary Tier *
        </label>
        <div className="relative">
          <select
            value={selectedTier}
            onChange={(e) => onTierChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
            required
          >
            <option value="">Select Vocabulary Tier</option>
            {tiers.map(tier => (
              <option key={tier.key} value={tier.key}>
                {tier.label} - {tier.vocabularyLevel}
              </option>
            ))}
          </select>
          <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Current Tier Display */}
      {currentTier && (
        <div className={`p-4 rounded-lg border ${currentTier.color.replace('text-', 'border-').replace('-800', '-200')}`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${currentTier.color}`}>
                  {currentTier.label}
                </span>
                <h4 className="font-medium text-gray-800">{currentTier.vocabularyLevel}</h4>
              </div>
              <p className="text-sm text-gray-600 mb-3">{currentTier.description}</p>
              
              {/* Expected Answer Level */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-700">Expected Answer Level:</p>
                <p className="text-xs text-gray-600">{currentTier.expectedAnswer}</p>
                <p className="text-xs font-medium text-gray-700 mt-2">Complexity:</p>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                  {currentTier.characteristics.complexity}
                </span>
              </div>
            </div>
            
            <button
              type="button"
              onClick={() => setShowHelper(!showHelper)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="View Tier Guide"
            >
              <SafeIcon icon={FiInfo} />
            </button>
          </div>
        </div>
      )}

      {/* Vocabulary Analysis */}
      {analysis && questionText && questionType && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg border ${
            analysis.isAligned 
              ? 'bg-green-50 border-green-200' 
              : 'bg-orange-50 border-orange-200'
          }`}
        >
          <div className="flex items-start space-x-2">
            <SafeIcon 
              icon={analysis.isAligned ? FiCheckCircle : FiAlertTriangle} 
              className={`mt-0.5 ${
                analysis.isAligned ? 'text-green-600' : 'text-orange-600'
              }`} 
            />
            <div className="flex-1">
              <h4 className={`text-sm font-medium ${
                analysis.isAligned ? 'text-green-800' : 'text-orange-800'
              }`}>
                {analysis.isAligned ? 'Tier Alignment Good' : 'Consider Tier Adjustment'}
              </h4>
              
              <div className="mt-2 space-y-2">
                {!analysis.isAligned && (
                  <div>
                    <p className="text-xs text-orange-700">
                      Suggested tier: <strong>{analysis.suggestion?.label}</strong>
                    </p>
                    <p className="text-xs text-orange-600 mt-1">
                      {analysis.suggestion?.description}
                    </p>
                  </div>
                )}
                
                <div className="text-xs text-gray-600">
                  <p><strong>Analysis confidence:</strong> {analysis.confidence}</p>
                  <p><strong>Question type impact:</strong> {questionType} (×{analysis.factors.typeWeight})</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Tier Guide Modal */}
      <AnimatePresence>
        {showHelper && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiLayers} className="text-primary-600" />
                    <h3 className="text-lg font-semibold text-gray-800">
                      Vocabulary Tier Guide
                    </h3>
                  </div>
                  <button
                    onClick={() => setShowHelper(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiChevronUp} />
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Understanding vocabulary complexity for effective question design
                </p>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  {tiers.map((tier, index) => (
                    <div key={tier.key} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedTier(expandedTier === tier.key ? null : tier.key)}
                        className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${tier.color}`}>
                              {tier.label}
                            </span>
                            <span className="font-medium text-gray-800">{tier.vocabularyLevel}</span>
                          </div>
                          <SafeIcon 
                            icon={expandedTier === tier.key ? FiChevronUp : FiChevronDown} 
                            className="text-gray-400" 
                          />
                        </div>
                        <p className="text-sm text-gray-600 mt-1 text-left">{tier.description}</p>
                      </button>

                      <AnimatePresence>
                        {expandedTier === tier.key && (
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: 'auto' }}
                            exit={{ height: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="p-4 bg-white border-t border-gray-200">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Characteristics */}
                                <div>
                                  <h4 className="text-sm font-medium text-gray-800 mb-2">
                                    Characteristics
                                  </h4>
                                  <div className="space-y-2 text-xs text-gray-600">
                                    <p><strong>Vocabulary:</strong> {tier.characteristics.vocabulary.join(', ')}</p>
                                    <p><strong>Complexity:</strong> {tier.characteristics.complexity}</p>
                                    <p><strong>Cognitive Load:</strong> {tier.characteristics.cognitive}</p>
                                  </div>
                                </div>

                                {/* Expected Answer */}
                                <div>
                                  <h4 className="text-sm font-medium text-gray-800 mb-2">
                                    Expected Answer Level
                                  </h4>
                                  <p className="text-xs text-gray-600">{tier.expectedAnswer}</p>
                                </div>
                              </div>

                              {/* Example Patterns */}
                              <div className="mt-4">
                                <h4 className="text-sm font-medium text-gray-800 mb-2">
                                  Common Question Patterns
                                </h4>
                                <div className="flex flex-wrap gap-1">
                                  {tier.characteristics.examples.map((example, idx) => (
                                    <span 
                                      key={idx} 
                                      className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                                    >
                                      {example}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              {/* Keywords */}
                              <div className="mt-4">
                                <h4 className="text-sm font-medium text-gray-800 mb-2">
                                  Key Indicators ({tier.keywords.length})
                                </h4>
                                <div className="flex flex-wrap gap-1">
                                  {tier.keywords.slice(0, 12).map(keyword => (
                                    <span 
                                      key={keyword} 
                                      className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs"
                                    >
                                      {keyword}
                                    </span>
                                  ))}
                                  {tier.keywords.length > 12 && (
                                    <span className="px-2 py-1 bg-gray-200 text-gray-600 rounded text-xs">
                                      +{tier.keywords.length - 12} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VocabularyTierHelper;